// Display formatting for the KPIs surface — FACTUAL arithmetic/formatting over the projection's recorded
// values only (no unit metadata exists on the Metric, so formatting is magnitude-based). The DELTA is the
// difference between the last two AUTHORITATIVE recorded values — real recorded points, not an agent-judged
// slope (the trend JUDGMENT stays direction-only, G-106; this is display math like the prototype's). The
// TARGET GAP is the same kind of fact: value minus target, STATED, never toned — the Metric records no
// direction-of-goodness, so "over"/"under" carries no good/bad colour anywhere it renders.

import type { MetricCard, MetricPoint } from '../projections.ts';

/** Format one recorded value for display: thousands grouped; small non-integers to one decimal. */
export function fmtMetricValue(value: number): string {
  if (Number.isInteger(value)) return value.toLocaleString('en-GB');
  return Math.abs(value) >= 100
    ? value.toLocaleString('en-GB', { maximumFractionDigits: 0 })
    : value.toFixed(1);
}

/** The factual delta between the last two recorded points, signed — or null with fewer than two. */
export function fmtMetricDelta(series: readonly MetricPoint[]): string | null {
  if (series.length < 2) return null;
  const last = series[series.length - 1]!.value;
  const prev = series[series.length - 2]!.value;
  const diff = last - prev;
  if (diff === 0) return '±0';
  return `${diff > 0 ? '+' : '−'}${fmtMetricValue(Math.abs(diff))}`;
}

/** A short day-month label ("05 Apr") from an ISO date, for chart axes and task rows. */
export function fmtDayMonth(isoDate: string): string {
  const parsed = new Date(`${isoDate.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return isoDate.slice(0, 10);
  return parsed.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', timeZone: 'UTC' });
}

/** The factual gap between a recorded value and the human target — stated, never judged. */
export function fmtTargetGap(value: number, target: number): string {
  const diff = value - target;
  if (diff === 0) return 'at target';
  return `${fmtMetricValue(Math.abs(diff))} ${diff > 0 ? 'over' : 'under'}`;
}

/** True when a Metric needs a human decision (proposed metric, or a proposed latest value). */
export function isAwaiting(metric: MetricCard): boolean {
  return metric.state === 'proposed' || metric.latestValue?.state === 'proposed';
}

/** The card status line — derived ONLY from projection lifecycle fields (G-182), like the Task card's. */
export function metricStatusLine(metric: MetricCard): string {
  if (metric.state === 'proposed') {
    return metric.activationReadiness?.ready === true
      ? 'proposed · awaiting activation'
      : 'proposed · needs setup';
  }
  if (metric.state === 'archived') return 'archived';
  const value = metric.latestValue;
  if (value === null) return 'active · no values yet';
  if (value.state === 'proposed') return 'proposed · awaiting you';
  return `confirmed · ${fmtDayMonth(value.periodEnd)}`;
}

/** Slice the charted window from the full series: the prototype's 6W / 12W / ALL ranges. */
export type ChartRange = '6w' | '12w' | 'all';
export function windowedSeries(
  series: readonly MetricPoint[],
  range: ChartRange,
): readonly MetricPoint[] {
  if (range === 'all') return series;
  return series.slice(-(range === '6w' ? 6 : 12));
}
